import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface S3Object {
  key: string;
  name: string;
  size?: number;
  lastModified?: string;
  isFolder: boolean;
}

export interface S3ListResponse {
  objects: S3Object[];
  prefix: string;
  continuationToken?: string;
}

/**
 * List objects in S3 bucket at specified prefix
 */
export async function listS3Objects(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  prefix: string = 'prod/raw/allure/',
  continuationToken?: string,
): Promise<S3ListResponse> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('qa-automated-tests');
    const params = new URLSearchParams({
      prefix,
      ...(continuationToken && { continuationToken }),
    });
    
    const response = await fetchApi.fetch(
      `${baseUrl}/s3/objects?${params}`,
    );

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn('[QA Plugin] Failed to list S3 objects:', response.status);
      return { objects: [], prefix };
    }

    return response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[QA Plugin] Error listing S3 objects:', error);
    return { objects: [], prefix };
  }
}

/**
 * Generate a presigned URL for downloading an S3 object
 */
export async function getPresignedDownloadUrl(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  key: string,
): Promise<string | null> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('qa-automated-tests');
    const response = await fetchApi.fetch(
      `${baseUrl}/s3/download-url?${new URLSearchParams({ key })}`,
    );

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn('[QA Plugin] Failed to get presigned URL:', response.status);
      return null;
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[QA Plugin] Error getting presigned URL:', error);
    return null;
  }
}

/**
 * Parse S3 key to extract folder structure
 */
export function parseS3Key(key: string): { folders: string[]; fileName: string } {
  const parts = key.split('/').filter(Boolean);
  const fileName = parts[parts.length - 1] || '';
  const folders = parts.slice(0, -1);
  
  return { folders, fileName };
}

/**
 * Get breadcrumb path from prefix
 */
export function getBreadcrumbPath(prefix: string): Array<{ name: string; path: string }> {
  const parts = prefix.split('/').filter(Boolean);
  const breadcrumbs: Array<{ name: string; path: string }> = [];
  let currentPath = '';
  
  for (const part of parts) {
    currentPath += `${part}/`;
    breadcrumbs.push({
      name: part,
      path: currentPath,
    });
  }
  
  return breadcrumbs;
}

/**
 * Format file size to human readable
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return '-';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * Format date to human readable
 */
export function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  
  return new Date(dateString).toLocaleString();
}
